import { Role } from "@/services/rolesPermissionsService";
import { PERMISSIONS } from "./permissions.constants";
import { Usuario } from "./types";
import { FEATURES } from "@/config/features";
export { PERMISSIONS } from "./permissions.constants";


const isFullAccess = (permission: string) => {
  return permission === PERMISSIONS.FULL_ACCESS.name;
};

const hasPermission = (userPermissions: string[], permissionName: string) => {
  return userPermissions.some(p => isFullAccess(p) || p === permissionName);
};

const hasAnyPermission = (userPermissions: string[], permissionNames: string[]) => {
  return permissionNames.some(permissionName => hasPermission(userPermissions, permissionName));
};

const hasAllPermissions = (userPermissions: string[], permissionNames: string[]) => {
  return permissionNames.every(permissionName => hasPermission(userPermissions, permissionName));
}

export const checkAnyPermissions = (userPermissions: string[], permission: string | string[]) => {
  if (typeof permission === 'string') {
    return hasPermission(userPermissions, permission);
  } else if (Array.isArray(permission)) {
    return hasAnyPermission(userPermissions, permission);
  }
  return false;
};

export const checkAllPermissions = (userPermissions: string[], permission: string | string[]) => {
  if (typeof permission === 'string') {
    return hasPermission(userPermissions, permission);
  } else if (Array.isArray(permission)) {
    return hasAllPermissions(userPermissions, permission);
  }
  return false;
};

/** Archivar requiere el permiso y que la feature esté prendida. */
export const canArchivarEscrituras = (userPermissions: string[]) => {
  return FEATURES.archivadoEscrituras
    && checkAllPermissions(userPermissions, [PERMISSIONS.MANAGE_ARCHIVADO_TRAMITE.name]);
};

export const checkAnyPermissionsFromSubtramite = (
  user: Usuario,
  permission: string | string[],
  tramite: any,
  subtramiteKey: string,
  readonly = false
) => {
  const userPermissions = user?.permissions || [];
  const compradores = tramite?.compradores ?? [];
  const vendedores = tramite?.vendedores ?? [];

  const hasPermission = checkAnyPermissions(userPermissions, permission);

  if (hasPermission) return true;

  const isComprador: boolean = compradores.some(c => c.email === user.email);
  const isVendedor: boolean = vendedores.some(v => v.email === user.email);


  if (isVendedor || isComprador) {
    const subtramite = tramite[subtramiteKey];
    const files: any[] = subtramite?.files || [];
    return files.some(f => {
      if (isVendedor) {
        return readonly ? (!!f.vendedor_read || !!f.vendedor_write) : !!f.vendedor_write;
      } else if (isComprador) {
        return readonly ? (!!f.comprador_read || !!f.comprador_write) : !!f.comprador_write;
      }
      return false;
    });
  }

  return false;
}

export const getPermissionDisplayName = (permission: string) => {
  const perm = Object.values(PERMISSIONS).find(p => p.name === permission);
  return perm ? perm.displayName : permission;
};

export const getPermissionDescription = (permission: string) => {
  const perm = Object.values(PERMISSIONS).find(p => p.name === permission);
  return perm ? perm.description : '';
};

export const getPermissionTags = (permission: string) => {
  const perm = Object.values(PERMISSIONS).find(p => p.name === permission);
  return perm ? perm.tags : [];
};

export const getAllPermissionsNames = () => {
  return Object.values(PERMISSIONS).map(p => p.name);
};

export const enrichUserPermissions = (user: Usuario, tramiteRoles: Role[], tramite?: any) => {
  const userPermissions = user?.originalPermissions || [];
  const compradores = tramite?.compradores ?? [];
  const vendedores = tramite?.vendedores ?? [];
  const isComprador: boolean = compradores.some(c => c.email === user?.email);
  const isVendedor: boolean = vendedores.some(v => v.email === user?.email);
  const compradorPermissions = tramiteRoles.find(r => r.name === 'comprador')?.permissions?.map(p => p.name) || [];
  const vendedorPermissions = tramiteRoles.find(r => r.name === 'vendedor')?.permissions?.map(p => p.name) || [];

  return {
    ...user,
    permissions: [
      ...userPermissions,
      ...(isComprador ? compradorPermissions : []),
      ...(isVendedor ? vendedorPermissions : [])
    ]
  }
};
